import { FormikErrors, FormikTouched } from 'formik';
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';

import { getFormikErrorFieldLabels } from './useFormikErrors.model';

export type UseFormikErrorsProps<TValues extends yup.Maybe<yup.AnyObject>> = Readonly<{
  readonly errors: FormikErrors<TValues>;
  readonly touched: FormikTouched<TValues>;
  readonly isSubmitting: boolean;
  readonly validationSchema: yup.ObjectSchema<TValues>;
}>;

export type UseFormikErrorsResult = Readonly<{
  readonly invalidFormFieldLabels: string[];
  readonly showInvalidFields: boolean;
}>;

/**
 * This hook is used to show a list form fields that have validation errors on submit.
 * @param errors
 * @param touched
 * @param isSubmitting
 * @param validationSchema
 */
export function useFormikErrors<TValues extends yup.Maybe<yup.AnyObject>>({
  errors,
  touched,
  isSubmitting,
  validationSchema,
}: UseFormikErrorsProps<TValues>): UseFormikErrorsResult {
  const [invalidFormFieldLabels, setInvalidFormFieldLabels] = useState<string[]>([]);

  /**
   * Keep the latest `errors`/`validationSchema` available synchronously during render (i.e.
   * before any effects run), so the "submit finished" effect below can always read the
   * truly-current values. This matters because React 18 can batch the render where Formik
   * sets `isSubmitting` back to `false` together with the render where the resulting
   * validation `errors` are set (and, separately, unrelated field-level validation can
   * update `errors` while a submit is still in flight) — reacting to `errors` changes
   * directly (as we did previously) can therefore capture stale or premature values.
   */
  const latestErrorsRef = useRef(errors);
  latestErrorsRef.current = errors;
  const latestValidationSchemaRef = useRef(validationSchema);
  latestValidationSchemaRef.current = validationSchema;

  const wasSubmittingRef = useRef(isSubmitting);

  /**
   * When a submit attempt finishes (`isSubmitting` transitions from `true` to `false`), we
   * want to show all the errors that are present at that moment.
   */
  useEffect(() => {
    const wasSubmitting = wasSubmittingRef.current;
    wasSubmittingRef.current = isSubmitting;

    if (!wasSubmitting || isSubmitting) {
      return;
    }

    const submitValidationErrors = getFormikErrorFieldLabels(
      latestErrorsRef.current,
      latestValidationSchemaRef.current
    );
    setInvalidFormFieldLabels(submitValidationErrors);
  }, [isSubmitting]);

  /**
   * When the user fixes an error, we want to remove it from the list of errors.
   * Exclude all re-occurring errors that were present on last submit but were already fixed.
   * Exclude all new errors that were not present on last submit.
   */
  useEffect(() => {
    if (invalidFormFieldLabels.length === 0) {
      return;
    }

    const currInvalidFormFieldLabels = getFormikErrorFieldLabels(errors, validationSchema);
    const newInvalidFormFieldLabels = intersection(
      invalidFormFieldLabels,
      currInvalidFormFieldLabels
    );

    const diff = difference(invalidFormFieldLabels, newInvalidFormFieldLabels);

    if (diff.length) {
      setInvalidFormFieldLabels(newInvalidFormFieldLabels);
    }
  }, [errors, invalidFormFieldLabels, validationSchema]);

  const showInvalidFields = useMemo(() => {
    return !isEmpty(touched) && !!invalidFormFieldLabels.length;
  }, [invalidFormFieldLabels.length, touched]);

  return {
    invalidFormFieldLabels,
    showInvalidFields,
  };
}
